import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { parseServerError } from "utils/errors";
import callApi from "helpers/callApi";
import { getCookie } from "utils/cookie";

export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user", "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchApp = createAsyncThunk(
  "user/fetchApp",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/users/app_id/", "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);
export const switchApp = createAsyncThunk(
    "user/switchApp",
    async (id, { rejectWithValue }) => {
      try {
        const response = await callApi(`/users/app_id/?app_id=${id}`, "PUT");
        if (!response.ok) {
          let message = "Server error";
          try {
            const data = await response.json();
            message = data?.error || data?.message || message;
          } catch (_) {
            if (response.status === 401) message = "Session expired or unauthorized. Please log in again.";
            else if (response.status === 403) message = "You don't have access to this app.";
            else if (response.status >= 500) message = "Server error. Please try again later.";
          }
          return rejectWithValue(message);
        }
        return await response.json();
      } catch (err) {
        return rejectWithValue(err?.message || "Failed to switch app. Please try again.");
      }
    }
);

export const changeOwnPassword = createAsyncThunk(
  "user/changeOwnPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/password", "POST", data);
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const editNotifications = createAsyncThunk(
  "user/editNotifications",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/notifications/", "POST", data);
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "user/fetchNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/notifications/", "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchCompanyDefaultLanguage = createAsyncThunk(
  "user/fetchCompanyDefaultLanguage",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/company/lang", "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// export const registerUser = createAsyncThunk(
//   "user/registerUser",
//   async (data, { rejectWithValue }) => {
//     try {
//       const response = await callApi("/user/reg", "POST", data);
//       if (!response.ok) {
//         throw new Error("Server error");
//       }
//       return await response.json();
//     } catch (err) {
//       return rejectWithValue(err.message);
//     }
//   }
// );
export const registerUser = createAsyncThunk(
    "user/registerUser",
    async (data, { rejectWithValue }) => {
      try {
        const response = await callApi("/user/reg", "POST", data);
        const result = await response.json();
        if (!response.ok) {
          		// result is an object, for example { error: "User with this email already exists." }
          return rejectWithValue(result);
        }
        return result;
      } catch (err) {
        		// In case there's no response from the server at all
        return rejectWithValue({ error: err.message });
      }
    }
);

export const activateResend = createAsyncThunk(
  "user/activateResend",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/activate/send", "POST", data);
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/users", "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createUser = createAsyncThunk(
  "user/createUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/users", "POST", data);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.error || errorData.message || "Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message || "Server error");
    }
  }
);

export const updateMember = createAsyncThunk(
  "user/updateMember",
  async ({ userId, role, app_country }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/invitation/member/${userId}`, "PUT", { role, app_country });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.error || errorData.message || errorData.detail || "Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message || "Server error");
    }
  }
);

export const editUser = createAsyncThunk(
  "user/editUser",
  async ({ id, email, first_name, last_name, avatarFile, removeAvatar }, { rejectWithValue }) => {
    try {
      const data = {};
      if (email !== undefined && email !== null) data.email = email;
      if (first_name !== undefined && first_name !== null) data.first_name = first_name;
      if (last_name !== undefined && last_name !== null) data.last_name = last_name;
      if (removeAvatar === true) {
        data.clear_picture = true;
      }

      const fd = new FormData();
      fd.append("data", JSON.stringify(data));
      
      if (avatarFile) {
        fd.append("image", avatarFile);
      }

      const response = await callApi(`/user/${id}`, "POST", fd, {
        contentType: "multipart/form-data",
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || "Server error";
        return rejectWithValue(errorMessage);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (arg, { rejectWithValue }) => {
    const userId =
      arg != null && typeof arg === "object" ? arg.userId ?? arg.id : arg;

    if (userId == null || userId === "") {
      return rejectWithValue({ message: "Failed to remove member" });
    }

    try {
      const res = await callApi(`/user/${userId}`, "DELETE");
      if (res.ok) return true;
      const err = await res.json?.().catch(() => null);
      return rejectWithValue(err || { message: "Failed to remove member" });
    } catch (e) {
      return rejectWithValue({ message: e?.message || "Failed to remove member" });
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "user/changeUserPassword",
  async ({ id, new_password }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/user/${id}/password`, "POST", {
        new_password,
      });
      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || "Server error";
        return rejectWithValue(errorMessage);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const changeCompanyInfo = createAsyncThunk(
  "user/changeCompanyInfo",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/company/change/company_info`,
        "POST",
        data,
        {
          contentType: "multipart/form-data",
        }
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchCompanyDefaultCurrency = createAsyncThunk(
  "user/fetchCompanyDefaultCurrency",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/company/get_company_default_currency`,
        "GET"
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const switchCompanyDefaultCurrency = createAsyncThunk(
  "user/switchCompanyDefaultCurrency",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/company/edit_company_default_currency`,
        "POST",
        data
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const switchCompanyDefaultLanguage = createAsyncThunk(
  "user/switchCompanyDefaultLanguage",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/company/lang", "PUT", data);
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);


export const fetchUserGuide = createAsyncThunk(
  "user/fetchUserGuide",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/user_guide", "GET");
      
      if (!response.ok) {
        throw new Error("Server error");
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const skipStepUserGuide = createAsyncThunk(
  "user/skipStepUserGuide",
  async (step, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/user/user_guide_skip_step?step=${step}`,
        "GET"
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const getInvoiceGuideUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return '/api/1/app/l_invoice/user_guide';
  }
  
  if (hostname.includes('invoice')) {
    return `${protocol}//${hostname}/api/1/app/l_invoice/user_guide`;
  }
  
  const baseUrl = process.env.REACT_APP_API_URL || 'https://invoice.dev.lappa.org';
  return `${baseUrl}/api/1/app/l_invoice/user_guide`;
};

const getInvoiceGuideSkipUrl = (step) => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return `/api/1/app/l_invoice/user_guide_skip_step?step=${step}`;
  }
  
  if (hostname.includes('invoice')) {
    return `${protocol}//${hostname}/api/1/app/l_invoice/user_guide_skip_step?step=${step}`;
  }
  
  const baseUrl = process.env.REACT_APP_API_URL || 'https://invoice.dev.lappa.org';
  return `${baseUrl}/api/1/app/l_invoice/user_guide_skip_step?step=${step}`;
};

const callInvoiceGuideApi = async (url, method = "GET", payload = {}) => {
  const token2 = getCookie("API-KEY");
  const token = getCookie("api_key");
  
  const apiHeaders = (token || token2) ? { "api-key": token || token2 } : {};
  
  const options = {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...apiHeaders,
    },
    body: method !== "GET" ? JSON.stringify(payload) : null,
  };
  
  const response = await fetch(url, options);
  return response;
};

export const fetchInvoiceGuide = createAsyncThunk(
  "user/fetchInvoiceGuide",
  async (_, { rejectWithValue }) => {
    try {
      const url = getInvoiceGuideUrl();
      const response = await callInvoiceGuideApi(url, "GET");
      
      if (!response.ok) {
        throw new Error("Server error");
      }
      
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const skipStepInvoiceGuide = createAsyncThunk(
  "user/skipStepInvoiceGuide",
  async (step, { rejectWithValue }) => {
    try {
      const url = getInvoiceGuideSkipUrl(step);
      const response = await callInvoiceGuideApi(url, "GET");
      
      if (!response.ok) {
        throw new Error("Server error");
      }
      
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const forgotComplete = createAsyncThunk(
  "user/forgotComplete",
  async ({ code, password }, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/forgot", "POST", {
        code,
        password,
      });
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const forgotStart = createAsyncThunk(
  "user/forgotStart",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/forgot/send", "POST", data);
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const toggleTwoFaAuth = createAsyncThunk(
  "user/toggleTwoFaAuth",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/mobile/on_off_two_factor_authentication/`,
        "GET"
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getUserCompanies = createAsyncThunk(
  "user/getUserCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(`/user/list_company/`, "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const changeUserCompany = createAsyncThunk(
  "user/changeUserCompany",
  async (company_id, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/user/set_company/?company_id=${company_id}`,
        "PUT"
      );
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addUserCompany = createAsyncThunk(
  "user/addUserCompany",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi(`/user/create_company/`, "POST", data);
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deactivateFirstEntry = createAsyncThunk(
  "user/deactivateFirstEntry",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(`/user/first_entry`, "POST");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const unsubscribeNewsletter = createAsyncThunk(
  "user/deactivateFirstEntry",
  async (token, { rejectWithValue }) => {
    try {
      const response = await callApi(`/unsubscribe/${token}`, "GET");
      if (!response.ok) {
        throw new Error("Server error");
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    user: {},
    users: [],
    is_manager: null,
    new_user_id: null,
    userGuide: {},
    notifications: {},
    userCompanies: [],
    wantIntegrateStripe: false,
    integrateStripeParams: null,
    openProjectName: 'LTax',
    app_Id: 0,
    switchingCurrency: false,
    switchingCurrencyError: null,
    togglingTwoFa: false,
    toggleTwoFaError: null,
    invoiceGuide: {},
    fetchingInvoiceGuide: false,
    fetchingInvoiceGuideError: null,
    skippingInvoiceStep: false,
    skippingInvoiceStepError: null,
    updatingMember: false,
    updatingMemberError: null,
    deleting: false,
    deletingError: null
  },
  reducers: {
    addIntegrateStripeParams(state, action) {
      state.wantIntegrateStripe = true;
      state.integrateStripeParams = {
        ...action.payload,
      };
    },
    clearIntegrateStripeParams(state, action) {
      state.wantIntegrateStripe = false;
      state.integrateStripeParams = null;
    },
    addProjectOpenNameForUserInfo(state, action) {
      state.openProjectName = action.payload
    },
    clearSwitchingCurrencyError(state) {
      state.switchingCurrencyError = null;
    },
    clearCompanyPaymentCard(state) {
      if (state.user?.company?.info?.card) {
        delete state.user.company.info.card;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.registering = true;
        state.registered = false;
        state.registerError = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.registering = false;
        state.registered = true;
        state.registerError = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.registering = false;
        state.registered = false;
        state.registerError = parseServerError(action.payload);
      })
      .addCase(activateResend.pending, (state) => {
        state.sending = true;
        state.sent = false;
        state.resendError = null;
      })
      .addCase(activateResend.fulfilled, (state) => {
        state.sending = false;
        state.sent = true;
        state.resendError = null;
      })
      .addCase(activateResend.rejected, (state, action) => {
        state.sending = false;
        state.sent = false;
        state.resendError = parseServerError(action.payload);
      })
      .addCase(fetchUser.pending, (state) => {
        state.fetching = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.fetching = false;
        state.user = action.payload;
        state.fetchingError = null;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.fetching = false;
        state.fetchingError = parseServerError(action.payload);
      })
      .addCase(fetchUsers.pending, (state) => {
        state.fetchingUsers = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.fetchingUsers = false;
        state.users = action.payload;
        state.fetchingUsersError = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.fetchingUsers = false;
        state.fetchingUsersError = parseServerError(action.payload);
      })
      .addCase(createUser.pending, (state) => {
        state.creating = true;
        state.new_user_id = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.creating = false;
        state.new_user_id = action.payload.user_id;
        state.creatingError = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.creating = false;
        state.creatingError = parseServerError(action.payload);
      })
      .addCase(updateMember.pending, (state) => {
        state.updatingMember = true;
        state.updatingMemberError = null;
      })
      .addCase(updateMember.fulfilled, (state) => {
        state.updatingMember = false;
        state.updatingMemberError = null;
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.updatingMember = false;
        state.updatingMemberError = parseServerError(action.payload);
      })
      .addCase(editUser.pending, (state) => {
        state.editing = true;
        state.editingError = null;
      })
      .addCase(editUser.fulfilled, (state) => {
        state.editing = false;
        state.editingError = null;
      })
      .addCase(editUser.rejected, (state, action) => {
        state.editing = false;
        state.editingError = parseServerError(action.payload);
      })
      .addCase(deleteUser.pending, (state) => {
        state.deleting = true;
        state.deletingError = null;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.deleting = false;
        state.deletingError = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleting = false;
        state.deletingError = parseServerError(action.payload);
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.changing = true;
        state.changingError = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.changing = false;
        state.changingError = null;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.changing = false;
        state.changingError = parseServerError(action.payload);
      })
      .addCase(forgotStart.pending, (state) => {
        state.forgotStart = true;
      })
      .addCase(forgotStart.fulfilled, (state) => {
        state.forgotStart = false;
        state.forgotStartError = null;
      })
      .addCase(forgotStart.rejected, (state, action) => {
        state.forgotStart = false;
        state.forgotStartError = parseServerError(action.payload);
      })
      .addCase(forgotComplete.pending, (state) => {
        state.forgotComplete = true;
      })
      .addCase(forgotComplete.fulfilled, (state) => {
        state.forgotComplete = false;
        state.forgotCompleteError = null;
      })
      .addCase(forgotComplete.rejected, (state, action) => {
        state.forgotComplete = false;
        state.forgotCompleteError = parseServerError(action.payload);
      })
      .addCase(changeCompanyInfo.pending, (state) => {
        state.changing = true;
        state.changingError = null;
      })
      .addCase(changeCompanyInfo.fulfilled, (state) => {
        state.changing = false;
        state.changingError = null;
      })
      .addCase(changeCompanyInfo.rejected, (state, action) => {
        state.changing = false;
        state.changingError = parseServerError(action.payload);
      })
      .addCase(fetchCompanyDefaultCurrency.pending, (state) => {
        state.fetchingCurrency = true;
      })
      .addCase(fetchCompanyDefaultCurrency.fulfilled, (state, action) => {
        state.fetchingCurrency = false;
        state.defaultCurrency = action.payload;
        state.fetchingCurrencyError = null;
      })
      .addCase(fetchCompanyDefaultCurrency.rejected, (state, action) => {
        state.fetchingCurrency = false;
        state.defaultCurrency = null;
        state.fetchingCurrencyError = parseServerError(action.payload);
      })
      .addCase(fetchCompanyDefaultLanguage.pending, (state) => {
        state.fetchingLanguage = true;
      })
      .addCase(fetchCompanyDefaultLanguage.fulfilled, (state, action) => {
        state.fetchingLanguage = false;
        state.defaultLanguage = action.payload;
        state.fetchingLanguageError = null;
      })
      .addCase(fetchCompanyDefaultLanguage.rejected, (state, action) => {
        state.fetchingLanguage = false;
        state.defaultLanguage = "en";
        state.fetchingLanguageError = parseServerError(action.payload);
      })
      .addCase(switchCompanyDefaultCurrency.pending, (state) => {
        state.switchingCurrency = true;
      })
      .addCase(switchCompanyDefaultCurrency.fulfilled, (state) => {
        state.switchingCurrency = false;
        state.switchingCurrencyError = null;
      })
      .addCase(switchCompanyDefaultCurrency.rejected, (state, action) => {
        state.switchingCurrency = false;
        state.switchingCurrencyError = parseServerError(action.payload);
      })
      .addCase(switchCompanyDefaultLanguage.pending, (state) => {
        state.switchingLanguage = true;
      })
      .addCase(switchCompanyDefaultLanguage.fulfilled, (state, action) => {
        state.defaultLanguage = action.payload;
        state.switchingLanguage = false;
        state.switchingLanguageError = null;
      })
      .addCase(switchCompanyDefaultLanguage.rejected, (state, action) => {
        state.switchingLanguage = false;
        state.switchingLanguageError = parseServerError(action.payload);
      })
      .addCase(fetchUserGuide.pending, (state) => {
        state.fetchingGuide = true;
      })
      .addCase(fetchUserGuide.fulfilled, (state, action) => {
        state.fetchingGuide = false;
        state.userGuide = action.payload;
        state.fetchingGuideError = null;
      })
      .addCase(fetchUserGuide.rejected, (state, action) => {
        state.fetchingGuide = false;
        state.fetchingGuideError = parseServerError(action.payload);
      })
      .addCase(skipStepUserGuide.pending, (state) => {
        state.skippingStep = true;
      })
      .addCase(skipStepUserGuide.fulfilled, (state) => {
        state.skippingStep = false;
        state.skippingStepError = null;
      })
      .addCase(skipStepUserGuide.rejected, (state, action) => {
        state.skippingStep = false;
        state.skippingStepError = parseServerError(action.payload);
      })
      .addCase(fetchInvoiceGuide.pending, (state) => {
        state.fetchingInvoiceGuide = true;
      })
      .addCase(fetchInvoiceGuide.fulfilled, (state, action) => {
        state.fetchingInvoiceGuide = false;
        state.invoiceGuide = action.payload;
        state.fetchingInvoiceGuideError = null;
      })
      .addCase(fetchInvoiceGuide.rejected, (state, action) => {
        state.fetchingInvoiceGuide = false;
        state.fetchingInvoiceGuideError = parseServerError(action.payload);
      })
      .addCase(skipStepInvoiceGuide.pending, (state) => {
        state.skippingInvoiceStep = true;
      })
      .addCase(skipStepInvoiceGuide.fulfilled, (state) => {
        state.skippingInvoiceStep = false;
        state.skippingInvoiceStepError = null;
      })
      .addCase(skipStepInvoiceGuide.rejected, (state, action) => {
        state.skippingInvoiceStep = false;
        state.skippingInvoiceStepError = parseServerError(action.payload);
      })
      .addCase(editNotifications.pending, (state) => {
        state.fetchingNotifications = true;
      })
      .addCase(editNotifications.fulfilled, (state, action) => {
        state.fetchingNotifications = false;
        state.notifications = action.payload;
        state.fetchingNotificationsError = null;
      })
      .addCase(editNotifications.rejected, (state, action) => {
        state.fetchingNotifications = false;
        state.fetchingNotificationsError = parseServerError(action.payload);
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.fetchingNotifications = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.fetchingNotifications = false;
        state.notifications = action.payload;
        state.fetchingNotificationsError = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.fetchingNotifications = false;
        state.fetchingNotificationsError = parseServerError(action.payload);
      })
      .addCase(getUserCompanies.pending, (state) => {
        state.fetchingUserCompanies = true;
        state.userCompanies = [];
        state.selectedCompanyId = null;
      })
      .addCase(getUserCompanies.fulfilled, (state, action) => {
        state.fetchingUserCompanies = false;
        state.userCompanies = action.payload.companies;
        state.selectedCompanyId = action.payload.selected_company_id;
        state.fetchingUserCompaniesError = null;
      })
      .addCase(getUserCompanies.rejected, (state, action) => {
        state.fetchingUserCompanies = false;
        state.userCompanies = [];
        state.fetchingUserCompaniesError = parseServerError(action.payload);
        state.selectedCompanyId = null;
      })
      .addCase(addUserCompany.pending, (state) => {
        state.addingUserCompany = true;
        state.addingUserCompanyError = null;
      })
      .addCase(addUserCompany.fulfilled, (state) => {
        state.addingUserCompany = false;
        state.addingUserCompanyError = null;
      })
      .addCase(addUserCompany.rejected, (state, action) => {
        state.addingUserCompany = false;
        state.addingUserCompanyError = parseServerError(action.payload);
      })
      .addCase(changeOwnPassword.pending, (state) => {
        state.changing = true;
        state.changingError = null;
      })
      .addCase(changeOwnPassword.fulfilled, (state) => {
        state.changing = false;
        state.changingError = null;
      })
      .addCase(changeOwnPassword.rejected, (state, action) => {
        state.changing = false;
        state.changingError = parseServerError(action.payload);
      })
      .addCase(unsubscribeNewsletter.pending, (state) => {
        state.loadingUnsubscribe = true;
        state.unsubscribedSuccessfully = false;
        state.loadingUnsubscribeError = null;
      })
      .addCase(unsubscribeNewsletter.fulfilled, (state) => {
        state.loadingUnsubscribe = false;
        state.unsubscribedSuccessfully = true;
        state.loadingUnsubscribeError = null;
      })
      .addCase(unsubscribeNewsletter.rejected, (state, action) => {
        state.loadingUnsubscribe = false;
        state.unsubscribedSuccessfully = false;
        state.loadingUnsubscribeError = parseServerError(action.payload);
      })
      .addCase(fetchApp.pending, (state) => {
        state.loadingApp = true;
        state.loadingAppError = null;
      })
      .addCase(fetchApp.fulfilled, (state, action) => {
        state.loadingApp = false;
        state.loadingAppError = null;
        // Support both number (0, 1, ...) and object ({ app_id: 0 }) from API
        const raw = action.payload;
        const appIdFromPayload = typeof raw === 'object' && raw !== null && 'app_id' in raw
          ? raw.app_id
          : raw;
        const appIdNum = Number(appIdFromPayload);
        state.app_Id = !isNaN(appIdNum) ? appIdNum : 0;

        // Sync localStorage with server (so "Select app" modal can show when server sent 0)
        const isLocalhost = window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname === '[::1]' ||
          /\.local$/.test(window.location.hostname);

        if (!isLocalhost) {
          localStorage.setItem('app_id', String(state.app_Id));
        }
      })
      .addCase(fetchApp.rejected, (state, action) => {
        state.loadingApp = false;
        state.loadingAppError = parseServerError(action.payload);
      })
      .addCase(toggleTwoFaAuth.pending, (state) => {
        state.togglingTwoFa = true;
        state.toggleTwoFaError = null;
      })
      .addCase(toggleTwoFaAuth.fulfilled, (state, action) => {
        state.togglingTwoFa = false;
        state.toggleTwoFaError = null;
        // Update user's 2FA status
        if (state.user.personal_details) {
          state.user.personal_details.is_two_factor_authentication = action.payload.is_two_factor_authentication;
        }
      })
      .addCase(toggleTwoFaAuth.rejected, (state, action) => {
        state.togglingTwoFa = false;
        state.toggleTwoFaError = parseServerError(action.payload);
      });
  },
});

export const {
  addIntegrateStripeParams,
  clearIntegrateStripeParams,
  addProjectOpenNameForUserInfo,
  clearSwitchingCurrencyError,
  clearCompanyPaymentCard,
} = userSlice.actions;

export default userSlice.reducer;
